import React, { useState, useEffect, useRef } from 'react';
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
  animate,
  useReducedMotion,
} from 'framer-motion';
import { useLang } from '../../context/LangContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };
const EASE = [0.16, 1, 0.3, 1];
const SHUTTER_EASE = [0.7, 0, 0.2, 1];

/**
 * <CinematicIntro> — first-visit-only fullscreen loader.
 *
 * Timeline (3.5s total, three acts):
 *   ACT I  · VOID    (0  – 0.6s)  — black + grain + corner tick + typewriter + counter starts
 *   ACT II · REVEAL  (0.6 – 2.2s) — logo emerges through SVG displacement + blur cleanup,
 *                                   horizontal sweep line, tagline fades in, Skip becomes clickable
 *   ACT III · DISSOLVE (2.2 – 3.5s) — white flash, micro-shake on all foreground items,
 *                                     UI fades out, shutter bands tear the overlay apart, onComplete
 *
 * Honours `prefers-reduced-motion` (single 0.4s fade), focus-traps Tab during play,
 * and gracefully falls back on the smaller band-count for mobile.
 */
export default function CinematicIntro({ onComplete }) {
  const { lang } = useLang();
  const t = T[lang]?.intro || T.fr.intro;
  const prefersReducedMotion = useReducedMotion();

  // 'void' → 'reveal' → 'dissolve'
  const [phase, setPhase] = useState('void');

  // Mobile detection (only used to halve the shutter band count)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIsMobile(window.matchMedia('(max-width: 640px)').matches);
  }, []);

  const N_BANDS = isMobile ? 8 : 14;

  // SVG distortion ref (imperative manipulation of feDisplacementMap scale attribute)
  const dispRef = useRef(null);
  const dispScale = useMotionValue(80);
  useMotionValueEvent(dispScale, 'change', (v) => {
    if (dispRef.current) dispRef.current.setAttribute('scale', String(v));
  });

  // Counter LCD: motion value driving a text node
  const progress = useMotionValue(0);
  const counterText = useTransform(progress, (v) => v.toFixed(3));

  // Master timeline + focus trap
  useEffect(() => {
    // Reduced-motion shortcut: short fade, no choreography
    if (prefersReducedMotion) {
      const id = setTimeout(() => onComplete?.(), 400);
      return () => clearTimeout(id);
    }

    // Drop focus and trap Tab while the intro plays
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const onKey = (e) => {
      if (e.key === 'Tab') e.preventDefault();
    };
    window.addEventListener('keydown', onKey);

    // Counter: 0.000 → 1.000 over 2s starting at t=0.2s
    const counterCtrl = animate(progress, 1, {
      duration: 2.0,
      ease: [0.42, 0, 0.58, 1],
      delay: 0.2,
    });

    // SVG displacement: 80 → 0 over 1.4s starting at t=0.8s
    const dispCtrl = animate(dispScale, 0, {
      duration: 1.4,
      ease: EASE,
      delay: 0.8,
    });

    // Phase progressions
    const idReveal   = setTimeout(() => setPhase('reveal'),   600);
    const idDissolve = setTimeout(() => setPhase('dissolve'), 2200);
    const idDone     = setTimeout(() => onComplete?.(),       3500);

    return () => {
      window.removeEventListener('keydown', onKey);
      counterCtrl.stop();
      dispCtrl.stop();
      clearTimeout(idReveal);
      clearTimeout(idDissolve);
      clearTimeout(idDone);
    };
    // onComplete is stable from parent; motion values are refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion]);

  // Reduced-motion render: bare fade overlay
  if (prefersReducedMotion) {
    return (
      <motion.div
        role="status"
        aria-live="polite"
        aria-label={t.loading}
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'var(--bg)',
        }}
      />
    );
  }

  const dissolving = phase === 'dissolve';
  const logoWidth = isMobile ? 'min(420px, 84vw)' : 'min(720px, 78vw)';
  const counterSize = isMobile ? 'clamp(22px, 6vw, 36px)' : 'clamp(28px, 4vw, 56px)';

  return (
    <motion.div
      role="status"
      aria-live="polite"
      aria-label={t.loading}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'var(--bg)',
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* — SVG distortion filter (off-DOM) — */}
      <svg aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <filter id="tsanga-distort" x="-20%" y="-20%" width="140%" height="140%">
            <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="2" seed="3" result="noise" />
            <feDisplacementMap ref={dispRef} in="SourceGraphic" in2="noise" scale="80" />
          </filter>
        </defs>
      </svg>

      {/* — Grain noise — */}
      <motion.div
        aria-hidden="true"
        className="noise"
        initial={{ opacity: 0 }}
        animate={{ opacity: dissolving ? 0 : 0.14 }}
        transition={{ duration: dissolving ? 0.25 : 0.3, delay: dissolving ? 0.25 : 0 }}
        style={{ position: 'absolute', inset: 0, opacity: 0.14 }}
      />

      {/* — ACT I — top-left vertical tick — */}
      <motion.div
        aria-hidden="true"
        initial={{ scaleY: 0 }}
        animate={dissolving ? { opacity: 0 } : { scaleY: 1 }}
        transition={dissolving
          ? { duration: 0.25, delay: 0.25 }
          : { duration: 0.5, ease: EASE, delay: 0.05 }
        }
        style={{
          position: 'absolute', top: 40, left: 40,
          width: 1, height: 24,
          background: 'rgba(255,255,255,0.45)',
          transformOrigin: 'top',
        }}
      />

      {/* — ACT I — bottom-left typewriter — */}
      <Typewriter
        text="TSANGA — A PIECE OF SKY · BXL"
        startDelay={0.1}
        charDelay={0.025}
        dissolving={dissolving}
        style={{
          position: 'absolute', bottom: 40, left: 40,
          fontFamily: 'var(--mono)', fontSize: 10,
          letterSpacing: '0.28em', color: 'var(--muted)',
          textTransform: 'uppercase', whiteSpace: 'nowrap',
        }}
      />

      {/* — ACT I — bottom-right loading label — */}
      <motion.span
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={dissolving ? { opacity: 0 } : { opacity: 0.55 }}
        transition={dissolving
          ? { duration: 0.25, delay: 0.25 }
          : { duration: 0.3, delay: 0.4 }
        }
        style={{
          position: 'absolute', bottom: 40, right: 40,
          fontFamily: 'var(--mono)', fontSize: 10,
          letterSpacing: '0.28em', color: 'var(--muted)',
          textTransform: 'uppercase',
        }}
      >
        v.2026 · {t.loading}
      </motion.span>

      {/* — ACT II — horizontal sweep line — */}
      <motion.div
        aria-hidden="true"
        initial={{ scaleX: 0, opacity: 1 }}
        animate={dissolving
          ? { opacity: 0, y: [0, -6, 0] }
          : { scaleX: 1 }
        }
        transition={dissolving
          ? { duration: 0.4 }
          : { duration: 0.7, ease: EASE, delay: 1.0 }
        }
        style={{
          position: 'absolute', top: '50%', left: 0, right: 0,
          height: 1, background: 'rgba(255,255,255,0.35)',
          transformOrigin: 'left',
          transform: 'translateY(-50%)',
        }}
      />

      {/* — Center stack: logo + counter + tagline — */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        {/* Logo (distorted PNG) */}
        <motion.img
          src="/tsangalogosansfond.png"
          alt=""
          aria-hidden="true"
          initial={{ opacity: 0, scaleX: 0.92, filter: 'url(#tsanga-distort) blur(24px)' }}
          animate={dissolving
            ? {
                opacity: [1, 1, 0],
                y: [0, -6, 0],
                filter: ['blur(0px)', 'blur(8px)', 'blur(8px)'],
              }
            : {
                opacity: 1,
                scaleX: 1,
                filter: ['url(#tsanga-distort) blur(24px)', 'url(#tsanga-distort) blur(0px)'],
              }
          }
          transition={dissolving
            ? { duration: 0.9, times: [0, 0.35, 1], ease: 'easeInOut' }
            : { duration: 1.4, delay: 0.8, ease: EASE }
          }
          style={{
            display: 'block', width: logoWidth, height: 'auto',
            mixBlendMode: 'screen',
            marginBottom: 60,
          }}
        />

        {/* Counter LCD */}
        <motion.span
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={dissolving ? { opacity: 0, y: [0, -6, 0] } : { opacity: 0.7 }}
          transition={dissolving
            ? { duration: 0.3, delay: 0.05 }
            : { duration: 0.3, delay: 0.2 }
          }
          style={{
            fontFamily: 'var(--mono)',
            fontSize: counterSize,
            color: 'var(--muted)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.08em',
          }}
        >
          {counterText}
        </motion.span>

        {/* Tagline */}
        <motion.p
          aria-hidden="true"
          initial={{ opacity: 0, y: 12 }}
          animate={dissolving
            ? { opacity: 0, y: [0, -6, 0] }
            : { opacity: 1, y: 0 }
          }
          transition={dissolving
            ? { duration: 0.3, delay: 0.05 }
            : { duration: 0.6, delay: 1.6, ease: EASE }
          }
          style={{
            marginTop: 28, marginBottom: 0,
            fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 300,
            fontSize: 'clamp(11px, 1.2vw, 13px)',
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.5)',
            textAlign: 'center',
          }}
        >
          {t.tagline}
        </motion.p>
      </div>

      {/* — Skip button (visible from t=1.4s) — */}
      <motion.button
        type="button"
        onClick={() => onComplete?.()}
        aria-label={t.skip}
        initial={{ opacity: 0 }}
        animate={dissolving ? { opacity: 0 } : { opacity: 0.6 }}
        whileHover={dissolving ? undefined : { opacity: 1 }}
        transition={dissolving
          ? { duration: 0.2 }
          : { duration: 0.3, delay: 1.4 }
        }
        style={{
          position: 'absolute', top: 28, right: 32,
          fontFamily: 'var(--mono)', fontSize: 10,
          letterSpacing: '0.28em', color: 'var(--muted)',
          textTransform: 'uppercase', background: 'none', border: 'none',
          cursor: 'none', padding: 0,
          zIndex: 10001,
        }}
      >
        {t.skip}
      </motion.button>

      {/* — ACT III — white flash — */}
      {dissolving && (
        <motion.div
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.85, 0] }}
          transition={{ duration: 0.22, times: [0, 0.4, 1], ease: 'easeInOut' }}
          style={{
            position: 'absolute', inset: 0, zIndex: 10000,
            background: '#ffffff',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* — ACT III — shutter bands — */}
      {dissolving && <ShutterBands count={N_BANDS} />}
    </motion.div>
  );
}

/* — — Typewriter — — */
function Typewriter({ text, startDelay = 0, charDelay = 0.03, dissolving, style }) {
  return (
    <motion.span
      aria-hidden="true"
      animate={dissolving ? { opacity: 0 } : { opacity: 1 }}
      transition={{ duration: 0.25, delay: dissolving ? 0.25 : 0 }}
      style={style}
    >
      {text.split('').map((c, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.08, delay: startDelay + i * charDelay }}
          style={{ display: 'inline-block' }}
        >
          {c === ' ' ? ' ' : c}
        </motion.span>
      ))}
    </motion.span>
  );
}

/* — — Shutter bands — — */
function ShutterBands({ count }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute', inset: 0, zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        pointerEvents: 'none',
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: 0 }}
          animate={{ y: i % 2 === 0 ? '-110%' : '110%' }}
          transition={{
            duration: 0.8,
            ease: SHUTTER_EASE,
            delay: 0.35 + i * 0.04,
          }}
          style={{
            flex: `0 0 ${100 / count}%`,
            background: 'var(--bg)',
            willChange: 'transform',
          }}
        />
      ))}
    </div>
  );
}
